import imageCompression from "browser-image-compression";

/**
 * Starts lazy loading of images using Intersection Observer.
 */
export default function startLaizyLoading(options = {}) {
  // Default options
  const config = {
    debug: process.env.NODE_ENV !== 'production', // Only log in non-production environments
    maxCacheSize: 50, // Maximum number of images to cache
    useWebP: true, // Convert images to WebP when supported
    useAVIF: true, // Convert images to AVIF when supported (higher priority than WebP)
    useProgressiveLoading: true, // Use low quality placeholders while loading
    useLocalStorage: true, // Cache in localStorage between sessions
    localStoragePrefix: 'img_cache_', // Prefix for localStorage keys
    localStorageMaxSize: 50 * 1024 * 1024, // 50MB limit for localStorage
    localStorageExpiry: 7 * 24 * 60 * 60 * 1000, // 7 days expiry
    priorityAttributeName: 'data-priority', // Attribute for marking high priority images
    responsive: true, // Enable responsive image sizing
    responsiveSizes: {
      xs: 480,  // Extra small devices (portrait phones)
      sm: 768,  // Small devices (landscape phones)
      md: 992,  // Medium devices (tablets)
      lg: 1200, // Large devices (desktops)
      xl: 1600  // Extra large devices (large desktops)
    },
    prefetchDistance: 800, // Pixels from viewport to start prefetching images
    predictivePrefetch: true, // Use predictive prefetching based on scroll pattern
    showStats: false, // Show statistics widget
    optimizeCssBackgrounds: true, // Optimize CSS background images
    useSrcSet: true, // Use srcset for responsive images
    serverOptimizationEndpoint: '', // URL for server-side optimization API
    ...options
  };
  
  // Utility logging function that respects debug setting
  const log = (message, level = 'info', data = null) => {
    // Skip non-error logs in production
    if (!config.debug && level !== 'error') return;
    
    const timestamp = new Date().toISOString();
    const emoji = {
      info: '🔍',
      warn: '⚠️',
      error: '❌ 💥',
      success: '✅'
    }[level] || '📝';
    
    if (data) {
      console[level === 'error' ? 'error' : 'log'](`${emoji} [${timestamp}] ${message}`, data);
    } else {
      console[level === 'error' ? 'error' : 'log'](`${emoji} [${timestamp}] ${message}`);
    }
  };
  
  // Check browser support for image formats
  const imageFormats = {
    webp: (() => {
      try {
        return (document.createElement('canvas')
          .toDataURL('image/webp')
          .indexOf('data:image/webp') === 0);
      } catch (e) {
        return false;
      }
    })(),
    
    avif: (() => {
      try {
        // More robust check for AVIF support
        if ('HTMLPictureElement' in window && document.createElement('picture').hasOwnProperty('loading')) {
          // Modern check - create a test image
          const img = new Image();
          img.onload = () => true;
          img.onerror = () => false;
          img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
          return !img.complete ? true : img.width > 0;
        }
        return false;
      } catch (e) {
        return false;
      }
    })()
  };
  
  if (config.debug) {
    log(`🖼️ Image format support: ${JSON.stringify({
      WebP: imageFormats.webp ? '✅ Supported' : '❌ Not supported',
      AVIF: imageFormats.avif ? '✅ Supported' : '❌ Not supported'
    })}`);
  }
  
  // Get the current device width breakpoint
  const getDeviceWidthBreakpoint = () => {
    const width = window.innerWidth;
    const { responsiveSizes } = config;
    
    if (width < responsiveSizes.xs) return 'xxs';
    if (width < responsiveSizes.sm) return 'xs';
    if (width < responsiveSizes.md) return 'sm';
    if (width < responsiveSizes.lg) return 'md';
    if (width < responsiveSizes.xl) return 'lg';
    return 'xl';
  };
  
  // Get appropriate image dimension based on device width
  const getResponsiveImageDimension = (originalWidth, originalHeight) => {
    if (!config.responsive) {
      return { width: originalWidth, height: originalHeight };
    }
    
    const breakpoint = getDeviceWidthBreakpoint();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Determine maximum dimension based on breakpoint
    let maxDimension;
    switch (breakpoint) {
      case 'xxs': maxDimension = Math.min(screenWidth, 320); break;
      case 'xs': maxDimension = Math.min(screenWidth, 480); break;
      case 'sm': maxDimension = Math.min(screenWidth, 768); break;
      case 'md': maxDimension = Math.min(screenWidth, 992); break;
      case 'lg': maxDimension = Math.min(screenWidth, 1200); break;
      case 'xl': maxDimension = Math.min(screenWidth, 1600); break;
      default: maxDimension = Math.min(screenWidth, 1920);
    }
    
    // Calculate scaled dimensions maintaining aspect ratio
    const aspectRatio = originalWidth / originalHeight;
    let width, height;
    
    if (originalWidth > originalHeight) {
      // Landscape image
      width = Math.min(originalWidth, maxDimension);
      height = Math.round(width / aspectRatio);
    } else {
      // Portrait or square image
      height = Math.min(originalHeight, screenHeight * 0.9); // Max 90% of screen height
      width = Math.round(height * aspectRatio);
      
      // If width is still too large, scale down
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.round(width / aspectRatio);
      }
    }
    
    log(`📏 Responsive sizing: Original ${originalWidth}x${originalHeight} -> Scaled ${width}x${height} for ${breakpoint}`);
    
    return { width, height };
  };
  
  // Local storage utilities
  const localStorageUtil = {
    isAvailable: (() => {
      try {
        const testKey = '__test_storage__';
        localStorage.setItem(testKey, testKey);
        localStorage.removeItem(testKey);
        return true;
      } catch (e) {
        return false;
      }
    })(),
    
    getItem: (key) => {
      if (!localStorageUtil.isAvailable || !config.useLocalStorage) return null;
      
      try {
        const item = localStorage.getItem(`${config.localStoragePrefix}${key}`);
        if (!item) return null;
        
        const parsed = JSON.parse(item);
        
        // Check if expired
        if (parsed.expiry && parsed.expiry < Date.now()) {
          localStorageUtil.removeItem(key);
          return null;
        }
        
        return parsed.data;
      } catch (e) {
        log(`Failed to retrieve item from localStorage: ${e.message}`, 'error');
        return null;
      }
    },
    
    setItem: (key, value, additionalMetadata = {}) => {
      if (!localStorageUtil.isAvailable || !config.useLocalStorage) return false;
      
      try {
        // Clean up if we're exceeding storage limit
        localStorageUtil.cleanupIfNeeded();
        
        const item = {
          data: value,
          timestamp: Date.now(),
          expiry: Date.now() + config.localStorageExpiry,
          ...additionalMetadata
        };
        
        localStorage.setItem(`${config.localStoragePrefix}${key}`, JSON.stringify(item));
        return true;
      } catch (e) {
        log(`Failed to store item in localStorage: ${e.message}`, 'error');
        return false;
      }
    },
    
    removeItem: (key) => {
      if (!localStorageUtil.isAvailable) return;
      
      try {
        localStorage.removeItem(`${config.localStoragePrefix}${key}`);
      } catch (e) {
        log(`Failed to remove item from localStorage: ${e.message}`, 'error');
      }
    },
    
    // Estimate localStorage size
    getStorageSize: () => {
      if (!localStorageUtil.isAvailable) return 0;
      
      let total = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(config.localStoragePrefix)) {
          total += localStorage.getItem(key).length * 2; // Approximate UTF-16 size
        }
      }
      return total;
    },
    
    // Clean up localStorage if needed
    cleanupIfNeeded: () => {
      if (!localStorageUtil.isAvailable) return;
      
      try {
        const currentSize = localStorageUtil.getStorageSize();
        
        if (currentSize > config.localStorageMaxSize * 0.9) {
          log(`LocalStorage near capacity (${(currentSize / 1024 / 1024).toFixed(2)}MB), cleaning up old items`);
          
          // Get all items with our prefix
          const items = [];
          
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith(config.localStoragePrefix)) {
              try {
                const data = JSON.parse(localStorage.getItem(key));
                items.push({
                  key: key,
                  timestamp: data.timestamp || 0,
                  size: localStorage.getItem(key).length * 2
                });
              } catch (e) {
                // Invalid item, remove it
                localStorage.removeItem(key);
              }
            }
          }
          
          // Sort by timestamp (oldest first)
          items.sort((a, b) => a.timestamp - b.timestamp);
          
          // Remove oldest items until we're under 80% capacity
          let removedCount = 0;
          let freedSpace = 0;
          
          while (items.length > 0 && (currentSize - freedSpace) > config.localStorageMaxSize * 0.8) {
            const item = items.shift();
            localStorage.removeItem(item.key);
            freedSpace += item.size;
            removedCount++;
          }
          
          log(`Cleaned up ${removedCount} old items, freed ${(freedSpace / 1024 / 1024).toFixed(2)}MB`);
        }
      } catch (e) {
        log(`Error cleaning localStorage: ${e.message}`, 'error');
      }
    }
  };
  
  // Performance analytics storage
  if (!window.__lazyLoadStats) {
    window.__lazyLoadStats = {
      compressedImages: 0,
      totalOriginalSize: 0,
      totalCompressedSize: 0,
      compressionTimes: [],
      loadTimes: [],
      networkSavings: 0,
      localStorageHits: 0,
      webpConversions: 0,
      avifConversions: 0,
      prefetchedImages: 0,
      responsiveResizes: 0,
      priorityLoads: 0,
      backgroundImagesOptimized: 0,
      predictivePrefetches: 0,
      serverOptimizedImages: 0
    };
  }
  const stats = window.__lazyLoadStats;
  
  // Mark as initialized to prevent duplicate initialization
  if (window.__lazyLoadingInitialized) {
    log('🔄 Lazy loading already initialized, skipping');
    return;
  }
  
  window.__lazyLoadingInitialized = true;
  log('🔍 Initializing lazy loading for images...');
  
  // Create a cache for compressed images with LRU functionality
  if (!window.__imageCache) {
    window.__imageCache = {
      map: new Map(),
      keys: [], // To track order for LRU
      maxSize: config.maxCacheSize,
      activeBlobUrls: new Set(), // Track active blob URLs
      
      set(key, value) {
        // If the key already exists, remove it from the keys array
        const existingIndex = this.keys.indexOf(key);
        if (existingIndex !== -1) {
          this.keys.splice(existingIndex, 1);
          // Revoke the old blob URL if it exists
          const oldUrl = this.map.get(key);
          if (oldUrl && oldUrl.startsWith('blob:')) {
            this.activeBlobUrls.delete(oldUrl);
            URL.revokeObjectURL(oldUrl);
          }
        } else if (this.keys.length >= this.maxSize) {
          // Remove the least recently used item if we're at capacity
          const lruKey = this.keys.shift();
          const lruUrl = this.map.get(lruKey);
          if (lruUrl && lruUrl.startsWith('blob:')) {
            this.activeBlobUrls.delete(lruUrl);
            URL.revokeObjectURL(lruUrl);
            log(`🧹 Cache full, removed least recently used image: ${lruKey}`);
          }
          this.map.delete(lruKey);
        }
        
        // Add the new key to the end (most recently used)
        this.keys.push(key);
        this.map.set(key, value);
        
        // Track blob URLs
        if (value && value.startsWith('blob:')) {
          this.activeBlobUrls.add(value);
        }
        
        return value;
      },
      
      get(key) {
        if (!this.map.has(key)) return undefined;
        
        // Move the key to the end (most recently used)
        const existingIndex = this.keys.indexOf(key);
        if (existingIndex !== -1) {
          this.keys.splice(existingIndex, 1);
          this.keys.push(key);
        }
        
        return this.map.get(key);
      },
      
      has(key) {
        return this.map.has(key);
      },
      
      forEach(callback) {
        this.map.forEach(callback);
      },
      
      delete(key) {
        if (!this.map.has(key)) return;
        
        // Get the blob URL to revoke it
        const url = this.map.get(key);
        if (url && url.startsWith('blob:')) {
          this.activeBlobUrls.delete(url);
          URL.revokeObjectURL(url);
        }
        
        // Remove from the keys array
        const index = this.keys.indexOf(key);
        if (index !== -1) {
          this.keys.splice(index, 1);
        }
        
        // Delete from the map
        this.map.delete(key);
      },
      
      clear() {
        // Revoke all blob URLs
        this.activeBlobUrls.forEach(url => {
          URL.revokeObjectURL(url);
        });
        this.activeBlobUrls.clear();
        
        this.map.clear();
        this.keys = [];
        
        log(`🧹 Cleared all cached images and revoked ${this.activeBlobUrls.size} blob URLs`);
      },
      
      // Get stats about the cache
      getStats() {
        return {
          size: this.keys.length,
          maxSize: this.maxSize,
          activeBlobUrls: this.activeBlobUrls.size,
          memoryUsage: 'Not available' // Browser doesn't expose this directly
        };
      }
    };
  }
  const imageCache = window.__imageCache;
  
  // Store image loading start times
  const loadingStartTimes = new Map();
  
  // Track images that are currently being processed - use a global reference to prevent race conditions
  if (!window.__processingImages) {
    window.__processingImages = new Set();
  }
  const processingImages = window.__processingImages;
  
  // Report performance stats
  const reportPerformanceStats = () => {
    if (!config.debug) return;
    
    if (stats.compressedImages > 0) {
      const totalOriginalMB = stats.totalOriginalSize / (1024 * 1024);
      const totalCompressedMB = stats.totalCompressedSize / (1024 * 1024);
      const savedMB = totalOriginalMB - totalCompressedMB;
      const avgCompressionRatio = stats.totalOriginalSize / stats.totalCompressedSize;
      const avgCompressionTime = stats.compressionTimes.reduce((a, b) => a + b, 0) / stats.compressionTimes.length;
      const avgLoadTime = stats.loadTimes.reduce((a, b) => a + b, 0) / stats.loadTimes.length;
      
      log(`📊 PERFORMANCE REPORT:`, 'info', {
        'Total Images Processed': stats.compressedImages,
        'Total Original Size': `${totalOriginalMB.toFixed(2)} MB`,
        'Total Compressed Size': `${totalCompressedMB.toFixed(2)} MB`,
        'Total Bandwidth Saved': `${savedMB.toFixed(2)} MB (${(savedMB / totalOriginalMB * 100).toFixed(1)}%)`,
        'Average Compression Ratio': `${avgCompressionRatio.toFixed(2)}x`,
        'Average Compression Time': `${avgCompressionTime.toFixed(2)} ms`,
        'Average Image Load Time': `${avgLoadTime.toFixed(2)} ms`,
        'WebP Conversions': stats.webpConversions,
        'AVIF Conversions': stats.avifConversions,
        'LocalStorage Cache Hits': stats.localStorageHits,
        'Prefetched Images': stats.prefetchedImages,
        'Responsive Resizes': stats.responsiveResizes,
        'Priority Loads': stats.priorityLoads,
        'Background Images Optimized': stats.backgroundImagesOptimized,
        'Predictive Prefetches': stats.predictivePrefetches,
        'Server-Optimized Images': stats.serverOptimizedImages,
        'Cache Size': `${imageCache.keys.length} / ${imageCache.maxSize} images`,
        'LocalStorage Used': localStorageUtil.isAvailable ? 
          `${(localStorageUtil.getStorageSize() / 1024 / 1024).toFixed(2)} MB` : 'Not available'
      });
      
      // Estimated real-world implications
      const avgMobileSpeed = 1; // 1 MB/s as a conservative estimate for mobile
      const timeSavedOnMobile = savedMB / avgMobileSpeed;
      
      log(`💰 ESTIMATED USER IMPACT:`, 'info', {
        'Time Saved on 3G': `${timeSavedOnMobile.toFixed(1)} seconds`,
        'Estimated Data Cost Saved': `$${(savedMB * 0.01).toFixed(2)}` // Approximate cost of data
      });
    }
  };
  
  // Helper function to get optimized compression options based on image URL, size and network
  const getCompressionOptions = (imgURL, fileSize, imgElement = null) => {
    const isJPEG = imgURL.toLowerCase().endsWith('.jpg') || imgURL.toLowerCase().endsWith('.jpeg');
    const isPNG = imgURL.toLowerCase().endsWith('.png');
    const isGIF = imgURL.toLowerCase().endsWith('.gif');
    
    // Get network conditions
    const network = getNetworkCondition();
    const isSaveData = network.saveData;
    const connectionType = network.type;
    
    log(`🌐 Network conditions: ${connectionType} (Save-Data: ${isSaveData ? 'On' : 'Off'})`);
    
    // Determine image dimensions for responsive sizing
    let originalWidth = 0, originalHeight = 0;
    let responsiveDimensions = { width: 0, height: 0 };
    
    if (imgElement && imgElement.naturalWidth > 0) {
      originalWidth = imgElement.naturalWidth;
      originalHeight = imgElement.naturalHeight;
      responsiveDimensions = getResponsiveImageDimension(originalWidth, originalHeight);
    }
    
    // Base options
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: responsiveDimensions.width || 1920,
      useWebWorker: true,
    };
    
    // Responsive sizing - if we have dimensions from the image element
    if (config.responsive && responsiveDimensions.width > 0) {
      options.maxWidthOrHeight = responsiveDimensions.width;
      stats.responsiveResizes++;
    }
    
    // Apply best format based on browser support
    if (config.useAVIF && imageFormats.avif && !isGIF) {
      options.fileType = 'image/avif';
      options.quality = 0.75;
      log(`🖼️ Converting to AVIF format`);
      stats.avifConversions++;
    } else if (config.useWebP && imageFormats.webp && !isGIF) {
      options.fileType = 'image/webp';
      options.quality = 0.8;
      log(`🖼️ Converting to WebP format`);
      stats.webpConversions++;
    }
    
    // Size-based adjustments
    const sizeKB = fileSize / 1024;
    
    if (sizeKB > 5000) {
      // Very large images
      options.maxSizeMB = 0.8;
      options.maxWidthOrHeight = options.maxWidthOrHeight || 1600;
    } else if (sizeKB < 500) {
      // Small images - be gentler with compression
      options.maxSizeMB = 0.4;
      options.maxWidthOrHeight = options.maxWidthOrHeight || 1200;
    }
    
    // Network-based adjustments
    if (isSaveData || connectionType === 'slow-2g' || connectionType === '2g') {
      // Aggressive compression for very slow connections
      options.maxSizeMB = options.maxSizeMB * 0.6;
      options.maxWidthOrHeight = Math.min(options.maxWidthOrHeight || 1200, 1200);
      options.quality = 0.6;
      log(`🐌 Slow connection detected, applying more aggressive compression`);
    } else if (connectionType === '3g') {
      // Moderate compression for 3G
      options.maxSizeMB = options.maxSizeMB * 0.8;
      options.quality = 0.7;
      log(`📱 3G connection detected, applying moderate compression`);
    } else if (connectionType === '4g' || connectionType === 'unknown') {
      // Standard compression for 4G or unknown
      // Keep default settings
    }
    
    // Format-specific adjustments (if not using WebP/AVIF)
    if (!options.fileType) {
      if (isJPEG) {
        options.fileType = 'image/jpeg';
        options.quality = options.quality || 0.75; // Good balance for JPEGs
      } else if (isPNG) {
        options.fileType = 'image/png';
        options.quality = options.quality || 0.85; // PNGs often have transparency, so higher quality
      } else if (isGIF) {
        // Minimal compression for GIFs to preserve animation
        options.maxSizeMB = Math.min(options.maxSizeMB * 1.5, 2);
        options.fileType = 'image/gif';
      }
    }
    
    return options;
  };
  
  // Create cache key based on URL and options
  const createCacheKey = (url, options) => {
    const typeKey = options.fileType ? options.fileType.split('/')[1] : 'orig';
    const qualityKey = options.quality ? Math.round(options.quality * 100) : 'def';
    const sizeKey = options.maxWidthOrHeight || 'full';
    
    return `${url}__${typeKey}_${qualityKey}_${sizeKey}`;
  };
  
  // Detect network conditions
  const getNetworkCondition = () => {
    // Check if the Network Information API is available
    if ('connection' in navigator) {
      const connection = navigator.connection;
      return {
        type: connection.effectiveType || 'unknown',
        downlink: connection.downlink || -1,
        rtt: connection.rtt || -1,
        saveData: connection.saveData || false
      };
    }
    
    return {
      type: 'unknown',
      downlink: -1,
      rtt: -1,
      saveData: false
    };
  };

  // Create stats UI widget if enabled
  const createStatsWidget = () => {
    if (!config.showStats) return;
    
    // Remove any existing widget
    const existingWidget = document.getElementById('lazyload-stats-widget');
    if (existingWidget) {
      existingWidget.remove();
    }
    
    // Create widget container
    const statsWidget = document.createElement('div');
    statsWidget.id = 'lazyload-stats-widget';
    statsWidget.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background-color: rgba(0, 0, 0, 0.85);
      color: white;
      padding: 12px;
      border-radius: 8px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 12px;
      z-index: 9999;
      max-width: 300px;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(4px);
      transition: all 0.3s ease;
      transform: translateY(0);
      opacity: 0.9;
      border: 1px solid rgba(255, 255, 255, 0.1);
    `;
    
    // Create header with title and controls
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      padding-bottom: 6px;
    `;
    
    // Title
    const title = document.createElement('div');
    title.textContent = '📊 Image Optimization Stats';
    title.style.cssText = `
      font-weight: bold;
      font-size: 13px;
    `;
    header.appendChild(title);
    
    // Controls
    const controls = document.createElement('div');
    controls.style.cssText = `
      display: flex;
      gap: 8px;
    `;
    
    // Toggle button
    const toggleButton = document.createElement('button');
    toggleButton.textContent = '▼';
    toggleButton.title = 'Minimize/Expand';
    toggleButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0 4px;
      font-size: 12px;
      opacity: 0.8;
    `;
    controls.appendChild(toggleButton);
    
    // Refresh button
    const refreshButton = document.createElement('button');
    refreshButton.textContent = '🔄';
    refreshButton.title = 'Refresh Stats';
    refreshButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0 4px;
      font-size: 12px;
      opacity: 0.8;
    `;
    controls.appendChild(refreshButton);
    
    // Close button
    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.title = 'Close';
    closeButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0 4px;
      font-size: 12px;
      opacity: 0.8;
    `;
    controls.appendChild(closeButton);
    
    header.appendChild(controls);
    statsWidget.appendChild(header);
    
    // Content container
    const contentDiv = document.createElement('div');
    contentDiv.id = 'lazyload-stats-content';
    statsWidget.appendChild(contentDiv);
    
    // Add to document
    document.body.appendChild(statsWidget);
    
    // Toggle button behavior
    let minimized = false;
    toggleButton.addEventListener('click', () => {
      minimized = !minimized;
      toggleButton.textContent = minimized ? '▲' : '▼';
      
      if (minimized) {
        contentDiv.style.display = 'none';
        statsWidget.style.width = 'auto';
        statsWidget.style.height = 'auto';
        statsWidget.style.padding = '8px 12px';
        statsWidget.style.opacity = '0.7';
      } else {
        contentDiv.style.display = 'block';
        statsWidget.style.width = '';
        statsWidget.style.height = '';
        statsWidget.style.padding = '12px';
        statsWidget.style.opacity = '0.9';
      }
    });
    
    // Close button behavior
    closeButton.addEventListener('click', () => {
      statsWidget.style.display = 'none';
      config.showStats = false;
    });
    
    // Refresh button behavior
    refreshButton.addEventListener('click', () => {
      updateStats();
    });
    
    // Make widget draggable
    let isDragging = false;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    
    header.style.cursor = 'move';
    header.addEventListener('mousedown', (e) => {
      isDragging = true;
      dragOffsetX = e.clientX - statsWidget.getBoundingClientRect().left;
      dragOffsetY = e.clientY - statsWidget.getBoundingClientRect().top;
      statsWidget.style.transition = 'none';
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      
      const x = e.clientX - dragOffsetX;
      const y = e.clientY - dragOffsetY;
      
      // Keep the widget within viewport bounds
      const maxX = window.innerWidth - statsWidget.offsetWidth;
      const maxY = window.innerHeight - statsWidget.offsetHeight;
      
      statsWidget.style.left = `${Math.max(0, Math.min(x, maxX))}px`;
      statsWidget.style.top = `${Math.max(0, Math.min(y, maxY))}px`;
      statsWidget.style.right = 'auto';
      statsWidget.style.bottom = 'auto';
    });
    
    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        statsWidget.style.transition = 'all 0.3s ease';
      }
    });
    
    // Update stats function
    const updateStats = () => {
      if (!contentDiv) return;
      
      // Skip if minimized
      if (minimized) return;
      
      // Calculate totals
      const totalOriginalMB = stats.totalOriginalSize / (1024 * 1024);
      const totalCompressedMB = stats.totalCompressedSize / (1024 * 1024);
      const savedMB = Math.max(0, totalOriginalMB - totalCompressedMB);
      const avgRatio = stats.totalOriginalSize > 0 ? (stats.totalOriginalSize / Math.max(1, stats.totalCompressedSize)).toFixed(1) : 0;
      const avgLoadTime = stats.loadTimes.length ? (stats.loadTimes.reduce((a, b) => a + b, 0) / stats.loadTimes.length).toFixed(0) : 0;
      
      // Get memory usage from cache if available
      const cacheStats = imageCache.getStats ? imageCache.getStats() : { size: imageCache.keys.length };
      
      // Format stats with better visuals
      contentDiv.innerHTML = `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: rgba(255,255,255,0.1); padding: 6px; border-radius: 4px; text-align: center;">
            <div style="font-size: 18px; font-weight: bold;">${stats.compressedImages}</div>
            <div style="font-size: 10px; opacity: 0.7;">Images Processed</div>
          </div>
          <div style="background: rgba(255,255,255,0.1); padding: 6px; border-radius: 4px; text-align: center;">
            <div style="font-size: 18px; font-weight: bold;">${avgRatio}x</div>
            <div style="font-size: 10px; opacity: 0.7;">Compression Ratio</div>
          </div>
        </div>
        
        <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
          <span>Bandwidth Saved:</span>
          <span style="font-weight: bold; color: #4CAF50;">${savedMB.toFixed(2)} MB</span>
        </div>
        
        <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
          <span>Avg Load Time:</span>
          <span style="font-weight: bold;">${avgLoadTime} ms</span>
        </div>
        
        <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
          <span>Format Conversions:</span>
          <span>${stats.avifConversions} AVIF, ${stats.webpConversions} WebP</span>
        </div>
        
        <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
          <span>Cache Hits:</span>
          <span>${stats.localStorageHits}</span>
        </div>
        
        <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
          <span>Background Images:</span>
          <span>${stats.backgroundImagesOptimized}</span>
        </div>
        
        <div style="margin-bottom: 4px; display: flex; justify-content: space-between;">
          <span>Prefetched:</span>
          <span>${stats.prefetchedImages + stats.predictivePrefetches}</span>
        </div>
        
        <div style="margin-top: 8px; font-size: 10px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.1); opacity: 0.7;">
          Cache: ${cacheStats.size}/${cacheStats.maxSize} images, ${cacheStats.activeBlobUrls || 0} active blobs
        </div>
      `;
    };
    
    // Initial update
    updateStats();
    
    // Set interval to update stats
    const updateInterval = setInterval(updateStats, 2000);
    
    // Clean up on unload
    window.addEventListener('beforeunload', () => {
      clearInterval(updateInterval);
    });
    
    return { updateStats };
  };
  
  // Prefetch images based on predicted scroll direction
  const predictivelyPrefetchImages = (direction, speed) => {
    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportBottom = window.scrollY + viewportHeight;
    
    // Predict how far user will scroll based on speed
    const predictedScroll = speed * 10; // Assume scrolling continues at this rate for a bit
    
    // Find all unloaded images
    const unloadedImages = document.querySelectorAll("img[data-src]:not([src]), img[data-src][src='']");
    if (!unloadedImages.length) return;
    
    // Track how many images we found to prefetch
    let prefetchCount = 0;
    
    // Select images to prefetch based on direction
    try {
      Array.from(unloadedImages).forEach(img => {
        // Skip if no data-src
        if (!img.dataset.src) return;
        
        // Skip if already being processed
        if (processingImages.has(img.dataset.src)) return;
        
        // Get position relative to viewport
        const rect = img.getBoundingClientRect();
        const absoluteTop = rect.top + window.scrollY;
        
        let shouldPrefetch = false;
        
        if (direction > 0) { // Scrolling down
          // Image is below viewport but within predicted scroll distance
          if (absoluteTop > viewportBottom && absoluteTop < viewportBottom + predictedScroll) {
            shouldPrefetch = true;
          }
        } else { // Scrolling up
          // Image is above viewport but within predicted scroll distance
          const absoluteBottom = absoluteTop + rect.height;
          if (absoluteBottom < window.scrollY && absoluteBottom > window.scrollY - predictedScroll) {
            shouldPrefetch = true;
          }
        }
        
        if (shouldPrefetch) {
          log(`🔮 Predictively prefetching image due to ${direction > 0 ? 'downward' : 'upward'} scroll: ${img.dataset.src}`);
          
          // Add a marker to indicate this is a predictive prefetch
          prefetchManager.addToPrefetchQueue(img.dataset.src, 2, true);
          prefetchCount++;
          
          // Limit the number of prefetches per scroll event
          if (prefetchCount >= 3) {
            return;
          }
        }
      });
      
      if (prefetchCount > 0) {
        log(`🔮 Added ${prefetchCount} images to predictive prefetch queue`);
      }
    } catch (error) {
      log(`Error during predictive prefetching: ${error.message}`, 'error');
    }
  };
  
  // Create prefetch manager
  const prefetchManager = {
    queue: [],
    processing: false,
    prefetchedUrls: new Set(),
    
    addToPrefetchQueue: (imgURL, priority = 0, isPredictive = false) => {
      // Skip if already prefetched or in processing set
      if (prefetchManager.prefetchedUrls.has(imgURL) || processingImages.has(imgURL)) {
        return;
      }
      
      // Add to prefetch queue with priority
      prefetchManager.queue.push({
        url: imgURL,
        priority,
        isPredictive,
        addedTime: Date.now()
      });
      
      // Sort queue by priority (higher first) and then by added time
      prefetchManager.queue.sort((a, b) => {
        if (a.priority !== b.priority) return b.priority - a.priority;
        return a.addedTime - b.addedTime;
      });
      
      // Start processing if not already running
      if (!prefetchManager.processing) {
        prefetchManager.processQueue();
      }
    },
    
    processQueue: async () => {
      // Stop if queue is empty
      if (prefetchManager.queue.length === 0) {
        prefetchManager.processing = false;
        return;
      }
      
      prefetchManager.processing = true;
      
      // Get next item from queue
      const nextItem = prefetchManager.queue.shift();
      const imgURL = nextItem.url;
      
      // Mark as prefetched to avoid duplicates
      prefetchManager.prefetchedUrls.add(imgURL);
      
      try {
        // Prefetch the image (just download it to cache)
        log(`🔍 Prefetching image: ${imgURL}`);
        
        // Get compression options based on estimated file size
        const options = getCompressionOptions(imgURL, 1000000); // Estimate 1MB
        const cacheKey = createCacheKey(imgURL, options);
        
        // Skip if already in memory cache
        if (imageCache.has(cacheKey)) {
          log(`⏩ Image already in memory cache, skipping prefetch: ${imgURL}`);
          prefetchManager.processQueue();
          return;
        }
        
        // Check localStorage cache
        if (config.useLocalStorage && localStorageUtil.isAvailable) {
          const cachedData = localStorageUtil.getItem(cacheKey);
          if (cachedData) {
            log(`⏩ Image already in localStorage cache, skipping prefetch: ${imgURL}`);
            prefetchManager.processQueue();
            return;
          }
        }
        
        // Fetch the image with a timeout to prevent long-hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        try {
          const response = await fetch(imgURL, { 
            priority: 'low',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`Server responded with status: ${response.status}`);
          }
          
          const file = await response.blob();
          
          // Process and cache the image
          const compressedFile = await imageCompression(file, options);
          
          // Store in memory cache
          const blobUrl = URL.createObjectURL(compressedFile);
          imageCache.set(cacheKey, blobUrl);
          
          // Update stats
          if (nextItem.priority > 0) {
            stats.priorityLoads++;
          } else if (nextItem.isPredictive) {
            stats.predictivePrefetches++;
          } else {
            stats.prefetchedImages++;
          }
          
          log(`✅ Prefetched and cached image: ${imgURL}`);
        } catch (fetchError) {
          if (fetchError.name === 'AbortError') {
            log(`⏱️ Prefetch timed out for: ${imgURL}`, 'warn');
          } else {
            log(`⚠️ Error fetching image during prefetch: ${fetchError.message}`, 'error');
          }
        }
      } catch (error) {
        log(`Error prefetching image: ${error.message}`, 'error');
      } finally {
        // Process next item from queue with a short delay
        setTimeout(() => prefetchManager.processQueue(), 100);
      }
    }
  };
  
  // Scan for images with data-priority attribute
  const findPriorityImages = () => {
    const prioritySelector = `img[${config.priorityAttributeName}]`;
    const priorityImages = document.querySelectorAll(prioritySelector);
    
    if (priorityImages.length) {
      log(`🔍 Found ${priorityImages.length} priority images`);
      
      priorityImages.forEach(img => {
        const priority = parseInt(img.getAttribute(config.priorityAttributeName) || "1", 10);
        
        // If the priority image has data-src, process it immediately with high priority
        if (img.dataset.src) {
          processPriorityImage(img, priority);
        }
      });
    }
  };
  
  // Process a high-priority image immediately
  const processPriorityImage = (imgElement, priority) => {
    if (!imgElement.dataset.src) return;
    
    const imgURL = imgElement.dataset.src;
    const imgId = imgElement.id || imgElement.alt || imgURL || 'priority-image';
    
    log(`⚡ Processing priority ${priority} image: ${imgId}`);
    stats.priorityLoads++;
    
    // Unobserve image if it's already being observed
    if (imageLoader && imgElement.dataset.observed === "true") {
      imageLoader.unobserve(imgElement);
    }
    
    // Mark as observed to prevent future observations
    imgElement.dataset.observed = "true";
    
    // Mark as being processed
    processingImages.add(imgURL);
    
    // Force immediate loading
    loadAndProcessImage(imgElement, priority);
  };
  
  // Check viewport to prefetch near-viewport images
  const checkViewportForPrefetching = () => {
    if (!config.prefetchDistance) return;
    
    // Find all unloaded images with data-src attribute
    const unloadedImages = document.querySelectorAll("img[data-src]:not([src]), img[data-src][src='']");
    
    // Skip if no unloaded images
    if (!unloadedImages.length) return;
    
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const prefetchDistance = config.prefetchDistance;
    
    // Check each image's position relative to viewport
    unloadedImages.forEach(img => {
      const rect = img.getBoundingClientRect();
      
      // Skip if too far above viewport
      if (rect.bottom < -prefetchDistance) return;
      
      // Skip if too far below viewport
      if (rect.top > viewportHeight + prefetchDistance) return;
      
      // Skip if too far to the left or right of viewport
      if (rect.right < -prefetchDistance || rect.left > viewportWidth + prefetchDistance) return;
      
      // Calculate priority based on distance to viewport
      // Closer = higher priority (max 5)
      let distance = Math.min(
        Math.abs(rect.top),
        Math.abs(rect.bottom - viewportHeight)
      );
      
      // Normalize distance to priority (0-5)
      const priority = Math.max(0, 5 - Math.floor(distance / 200));
      
      // Add to prefetch queue if has data-src
      if (img.dataset.src) {
        prefetchManager.addToPrefetchQueue(img.dataset.src, priority);
      }
    });
  };

  // Predictive prefetching based on scroll behavior
  const setupPredictivePrefetching = () => {
    if (!config.predictivePrefetch) return;
    
    // Skip if IntersectionObserver is not supported
    if (!('IntersectionObserver' in window)) return;
    
    // Keep track of scroll direction and speed
    let lastScrollY = window.scrollY;
    let scrollDirection = 0; // 0 = unknown, 1 = down, -1 = up
    let scrollSpeed = 0;
    let consecutiveDirectionCount = 0;
    const scrollHistory = [];
    const maxScrollHistory = 5;
    
    window.addEventListener('scroll', () => {
      // Calculate scroll direction and speed
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;
      
      // Update scroll history
      scrollHistory.unshift(delta);
      if (scrollHistory.length > maxScrollHistory) {
        scrollHistory.pop();
      }
      
      // Calculate average scroll speed from history
      scrollSpeed = scrollHistory.reduce((sum, val) => sum + Math.abs(val), 0) / scrollHistory.length;
      
      // Determine direction
      const newDirection = delta > 0 ? 1 : delta < 0 ? -1 : 0;
      
      // Check if direction changed
      if (newDirection !== 0) {
        if (newDirection === scrollDirection) {
          // Same direction, increment counter
          consecutiveDirectionCount++;
        } else {
          // Direction changed, reset counter
          scrollDirection = newDirection;
          consecutiveDirectionCount = 1;
        }
      }
      
      lastScrollY = currentScrollY;
      
      // If user is consistently scrolling in one direction and speed is significant,
      // prefetch images in that direction
      if (consecutiveDirectionCount >= 3 && scrollSpeed > 5) {
        predictivelyPrefetchImages(scrollDirection, scrollSpeed);
      }
    }, { passive: true });
    
    log(`🔮 Predictive prefetching enabled`);
  };
  
  // Load and process an image (shared function for normal and priority loading)
  const loadAndProcessImage = async ($img, priority = 0) => {
    const imgId = $img.id || $img.alt || $img.dataset.src || 'unknown-image';
    
    try {
      // Skip if already being processed or has src set (prevent duplicate processing)
      const imgURL = $img.dataset.src;
      
      if (processingImages.has(imgURL)) {
        log(`🔄 Image ${imgId} is already being processed, skipping duplicate`, 'warn');
        return;
      }
      
      // Check if the image already has a non-data-src src
      if ($img.src && $img.src !== '' && !$img.src.includes('data:') && $img.src !== imgURL) {
        log(`🔄 Image ${imgId} already has src set to ${$img.src}, skipping`, 'info');
        return;
      }
      
      // Mark this image URL as being processed
      processingImages.add(imgURL);
      log(`📝 Marked image ${imgId} as processing`);
      
      // Record start time for performance measurement
      loadingStartTimes.set($img, performance.now());
      
      // Get compression options to create cache key
      const network = getNetworkCondition();
      let options = getCompressionOptions(imgURL, 1000000, $img); // Initial options estimate
      const cacheKey = createCacheKey(imgURL, options);
      
      // Check memory cache first (fastest)
      if (imageCache.has(cacheKey)) {
        log(`🔄 Using memory cache for image ${imgId}`);
        
        // Set the src to the cached blob URL
        $img.src = imageCache.get(cacheKey);
        
        // Add load event listener
        $img.addEventListener('load', () => {
          const loadEnd = performance.now();
          const startTime = loadingStartTimes.get($img);
          const totalLoadTime = startTime ? (loadEnd - startTime).toFixed(2) : 'unknown';
          
          log(`🎉 Image ${imgId} loaded from memory cache in ${totalLoadTime}ms`);
          
          if (startTime) {
            stats.loadTimes.push(loadEnd - startTime);
          }
          
          loadingStartTimes.delete($img);
          processingImages.delete(imgURL); // Remove from processing set
        });
        
        return;
      }
      
      // Check localStorage cache next
      let cachedImageData = null;
      if (config.useLocalStorage && localStorageUtil.isAvailable) {
        cachedImageData = localStorageUtil.getItem(cacheKey);
        
        if (cachedImageData) {
          stats.localStorageHits++;
          log(`📦 Found image ${imgId} in localStorage cache`);
          
          // Convert data URL to blob
          try {
            // Apply placeholder immediately if there's a thumbnail
            if (cachedImageData.thumbnail) {
              applyPlaceholder($img, cachedImageData.thumbnail);
            }
            
            // Convert base64 to blob
            const res = await fetch(cachedImageData.dataUrl);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            
            // Store in memory cache too
            imageCache.set(cacheKey, blobUrl);
            
            // Set final src
            $img.src = blobUrl;
            
            // Add load event listener
            $img.addEventListener('load', () => {
              const loadEnd = performance.now();
              const startTime = loadingStartTimes.get($img);
              const totalLoadTime = startTime ? (loadEnd - startTime).toFixed(2) : 'unknown';
              
              log(`🎉 Image ${imgId} loaded from localStorage in ${totalLoadTime}ms`);
              
              if (startTime) {
                stats.loadTimes.push(loadEnd - startTime);
              }
              
              loadingStartTimes.delete($img);
              processingImages.delete(imgURL); // Remove from processing set
            });
            
            return;
          } catch (error) {
            log(`Error using cached image data: ${error.message}`, 'error');
            // Fall through to normal loading
          }
        }
      }
      
      // Check if server-side optimization is available
      if (config.serverOptimizationEndpoint && config.serverOptimizationEndpoint.length > 0) {
        try {
          // Construct server URL with optimization parameters
          const breakpoint = getDeviceWidthBreakpoint();
          let serverUrl = new URL(config.serverOptimizationEndpoint);
          
          // Add optimization parameters
          serverUrl.searchParams.append('url', encodeURIComponent(imgURL));
          serverUrl.searchParams.append('width', options.maxWidthOrHeight || 'auto');
          
          // Add format preference
          if (imageFormats.avif) {
            serverUrl.searchParams.append('format', 'avif');
          } else if (imageFormats.webp) {
            serverUrl.searchParams.append('format', 'webp');
          }
          
          // Add quality parameter
          serverUrl.searchParams.append('quality', (options.quality || 0.8) * 100);
          
          // Add device/screen info
          serverUrl.searchParams.append('screen', breakpoint);
          
          log(`🖥️ Using server-side optimization for ${imgId}: ${serverUrl.toString()}`);
          
          // Fetch optimized image from server
          const response = await fetch(serverUrl.toString());
          
          if (response.ok) {
            const optimizedBlob = await response.blob();
            const blobUrl = URL.createObjectURL(optimizedBlob);
            
            // Cache the server-optimized image
            imageCache.set(cacheKey, blobUrl);
            
            // Apply to image
            $img.src = blobUrl;
            
            // Update stats
            stats.serverOptimizedImages++;
            
            // Add load event listener
            $img.addEventListener('load', () => {
              const loadEnd = performance.now();
              const startTime = loadingStartTimes.get($img);
              const totalLoadTime = startTime ? (loadEnd - startTime).toFixed(2) : 'unknown';
              
              log(`🎉 Server-optimized image ${imgId} loaded in ${totalLoadTime}ms`);
              
              if (startTime) {
                stats.loadTimes.push(loadEnd - startTime);
              }
              
              loadingStartTimes.delete($img);
              processingImages.delete(imgURL); // Remove from processing set
            });
            
            return;
          }
        } catch (serverError) {
          log(`Error with server-side optimization: ${serverError.message}`, 'error');
          // Fall through to client-side processing
        }
      }
      
      // Need to fetch and process the image
      log(`🔗 Loading image with data-src: ${imgURL}`);
      
      // If this is a priority image, use higher fetch priority
      const fetchOptions = {};
      if (priority > 0) {
        fetchOptions.priority = 'high';
      }
      
      // Fetch the image file
      log(`📡 Fetching image from: ${imgURL}`);
      const fetchStart = performance.now();
      
      try {
        const response = await fetch(imgURL, fetchOptions);
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
        }
        const file = await response.blob();
        const fetchEnd = performance.now();
        
        log(`⏱️ Fetch completed in ${(fetchEnd - fetchStart).toFixed(2)}ms`);
        log(`📊 Original image size: ${(file.size / 1024).toFixed(2)} KB`);
        
        // Generate low quality placeholder for progressive loading
        let placeholderDataUrl = null;
        if (config.useProgressiveLoading) {
          placeholderDataUrl = await generateLQIP(file, imgURL);
          if (placeholderDataUrl) {
            applyPlaceholder($img, placeholderDataUrl);
            log(`🖼️ Applied low quality placeholder for ${imgId}`);
          }
        }
        
        // Update stats
        stats.totalOriginalSize += file.size;
        
        // Get optimized compression options based on the image
        options = getCompressionOptions(imgURL, file.size, $img);
        
        // Update cache key with actual file size-based options
        const finalCacheKey = createCacheKey(imgURL, options);
        
        log(`🗜️ Starting compression with options:`, options);
        const compressionStart = performance.now();
        
        try {
          const compressedFile = await imageCompression(file, options);
          const compressionEnd = performance.now();
          const compressionTime = compressionEnd - compressionStart;
          
          // Update stats
          stats.compressionTimes.push(compressionTime);
          stats.compressedImages++;
          stats.totalCompressedSize += compressedFile.size;
          stats.networkSavings += (file.size - compressedFile.size);
          
          // Track format conversion stats
          if (options.fileType === 'image/avif') {
            stats.avifConversions++;
          } else if (options.fileType === 'image/webp') {
            stats.webpConversions++;
          }
          
          log(`⏱️ Compression completed in ${compressionTime.toFixed(2)}ms`);
          log(`📊 Compressed image size: ${(compressedFile.size / 1024).toFixed(2)} KB`);
          log(`📉 Compression ratio: ${(file.size / compressedFile.size).toFixed(2)}x`);
          
          // Create a URL for the compressed image file
          const compressedURL = URL.createObjectURL(compressedFile);
          
          // Cache the result in memory
          imageCache.set(finalCacheKey, compressedURL);
          
          // Cache in localStorage if enabled and the image isn't too large
          if (config.useLocalStorage && localStorageUtil.isAvailable && compressedFile.size < 2 * 1024 * 1024) {
            // Convert to data URL for localStorage
            const reader = new FileReader();
            reader.onloadend = () => {
              localStorageUtil.setItem(finalCacheKey, {
                dataUrl: reader.result,
                size: compressedFile.size,
                width: $img.naturalWidth,
                height: $img.naturalHeight,
                fileType: options.fileType,
                thumbnail: placeholderDataUrl
              });
              log(`💾 Saved compressed image to localStorage (${(compressedFile.size / 1024).toFixed(2)} KB)`);
            };
            reader.readAsDataURL(compressedFile);
          }
          
          // If using srcset for responsive images and the image element supports it
          if (config.useSrcSet && 'srcset' in $img) {
            // Create srcset based on different sizes if we have natural dimensions
            if ($img.naturalWidth > 0) {
              const originalWidth = $img.naturalWidth;
              const originalHeight = $img.naturalHeight;
              const aspectRatio = originalWidth / originalHeight;
              
              // Generate sizes for srcset (50%, 100%, 200% of current size)
              const srcsetWidths = [
                Math.round(options.maxWidthOrHeight * 0.5),
                options.maxWidthOrHeight,
                Math.min(Math.round(options.maxWidthOrHeight * 2), originalWidth)
              ].filter(w => w > 0 && w <= originalWidth);
              
              // Set sizes attribute if not already set
              if (!$img.sizes) {
                $img.sizes = '(max-width: 768px) 100vw, 50vw';
              }
              
              // For now, we'll just use the main compressed image since we've already processed it
              // In a more advanced implementation, you would generate multiple sizes server-side
              $img.srcset = `${compressedURL} ${options.maxWidthOrHeight}w`;
            }
          }
          
          log(`🔄 Setting src from data-src. Original data-src: ${imgURL}`);
          
          // Set the src of the image element to the compressed image
          $img.src = compressedURL;
          log(`✅ New src set to Blob URL: ${compressedURL}`);
          
          // If we've processed a few images, report the stats
          if (stats.compressedImages % 3 === 0) {
            reportPerformanceStats();
          }
        } catch (compressionError) {
          log(`Error compressing image ${imgId}: ${compressionError.message}`, 'error', compressionError);
          // Fall back to using the original image if compression fails
          log(`⚠️ Falling back to uncompressed image for ${imgId}`, 'warn');
          
          // Still cache the original for future use
          const originalURL = URL.createObjectURL(file);
          imageCache.set(finalCacheKey, originalURL);
          
          $img.src = originalURL;
        }
      } catch (fetchError) {
        log(`Error fetching image ${imgId}: ${fetchError.message}`, 'error', fetchError);
        // If we can't fetch the image, try setting src directly as a fallback
        log(`⚠️ Falling back to direct src for ${imgId}`, 'warn');
        $img.src = imgURL;
        processingImages.delete(imgURL); // Remove from processing set in case of error
      }
      
      // Add load event listener to track when image is fully loaded
      $img.addEventListener('load', () => {
        const loadEnd = performance.now();
        const startTime = loadingStartTimes.get($img);
        const totalLoadTime = startTime ? (loadEnd - startTime).toFixed(2) : 'unknown';
        
        log(`🎉 Image ${imgId} fully loaded in ${totalLoadTime}ms`);
        
        if (startTime) {
          stats.loadTimes.push(loadEnd - startTime);
        }
        
        loadingStartTimes.delete($img);
        processingImages.delete(imgURL); // Remove from processing set
      });
      
      // Add error event listener
      $img.addEventListener('error', (err) => {
        log(`Error loading image ${imgId}`, 'error', err);
        loadingStartTimes.delete($img);
        processingImages.delete(imgURL); // Remove from processing set
      });
      
    } catch (error) {
      log(`Error in lazy loading process for image ${imgId}: ${error.message}`, 'error', error);
      loadingStartTimes.delete($img);
      // Make sure to remove from processing set in case of any error
      if ($img.dataset.src) {
        processingImages.delete($img.dataset.src);
      }
    }
  };
  
  // Create an Intersection Observer
  const imageLoader = new IntersectionObserver(async (entries, observer) => {
    // Process entries sequentially to avoid race conditions
    for (const entry of entries) {
      const $img = entry.target;
      const imgId = $img.id || $img.alt || $img.dataset.src || 'unknown-image';
      
      // If the entry is not intersecting, skip
      if (!entry.isIntersecting) continue;
      
      // Immediately unobserve to prevent duplicate triggering
      observer.unobserve($img);
      log(`👋 Preemptively stopped observing image ${imgId} to prevent duplicates`);
      
      // Make sure we have a valid data-src
      if (!$img.dataset.src) {
        log(`Missing data-src for image ${imgId}`, 'error');
        continue;
      }
      
      // Check for priority loading
      const hasPriority = $img.hasAttribute(config.priorityAttributeName);
      const priority = hasPriority ? parseInt($img.getAttribute(config.priorityAttributeName) || "1", 10) : 0;
      
      // Process the image
      await loadAndProcessImage($img, priority);
    }
  }, {
    // Single threshold with lower value to trigger earlier
    threshold: [0.01],
    rootMargin: `${config.prefetchDistance}px` // Increased margin to load images sooner
  });

  // Generate low quality placeholder
  const generateLQIP = async (file, imgURL) => {
    if (!config.useProgressiveLoading) return null;
    
    try {
      // For tiny images, don't generate a placeholder
      if (file.size < 10 * 1024) return null;
      
      const options = {
        maxSizeMB: 0.01, // Tiny placeholder, ~10KB
        maxWidthOrHeight: 20, // Very small dimensions
        useWebWorker: true,
        fileType: imgURL.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg',
        quality: 0.1 // Very low quality for placeholder
      };
      
      const placeholder = await imageCompression(file, options);
      const reader = new FileReader();
      
      return new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(placeholder);
      });
    } catch (error) {
      log(`Error generating placeholder: ${error.message}`, 'error');
      return null;
    }
  };
  
  // Apply placeholder while loading
  const applyPlaceholder = ($img, placeholder) => {
    if (!placeholder) return;
    
    // Create a temporary minimal CSS for the blur effect
    const style = document.createElement('style');
    const uniqueClass = `img-loading-${Math.floor(Math.random() * 1000000)}`;
    $img.classList.add(uniqueClass);
    
    style.textContent = `
      .${uniqueClass} {
        filter: blur(10px);
        transition: filter 0.3s ease-out;
      }
      .${uniqueClass}.loaded {
        filter: blur(0);
      }
    `;
    document.head.appendChild(style);
    
    // Set initial placeholder
    $img.src = placeholder;
    
    // When the full image loads, remove the blur
    const loadHandler = () => {
      $img.classList.add('loaded');
      
      // Clean up resources after transition
      setTimeout(() => {
        $img.classList.remove(uniqueClass, 'loaded');
        style.remove();
        $img.removeEventListener('load', loadHandler);
      }, 500);
    };
    
    $img.addEventListener('load', loadHandler);
  };
  
  // Optimize CSS background images
  const setupCSSBackgroundOptimization = () => {
    if (!config.optimizeCssBackgrounds) return;
    
    // Skip if MutationObserver is not supported
    if (!('MutationObserver' in window)) return;
    
    // Create a registry of processed background elements to avoid duplicates
    const processedElements = new WeakSet();
    
    // Function to extract background image URL from CSS
    const extractBackgroundImageUrl = (cssValue) => {
      if (!cssValue) return null;
      
      // Match url() pattern in CSS
      const match = cssValue.match(/url\(['"]?([^'"()]+)['"]?\)/i);
      return match ? match[1] : null;
    };
    
    // Process an element with background image
    const processBackgroundElement = async (element) => {
      // Skip if already processed
      if (processedElements.has(element)) return;
      
      try {
        // Get computed style
        const style = window.getComputedStyle(element);
        const backgroundImage = style.backgroundImage;
        
        // Skip if no background image or it's 'none'
        if (!backgroundImage || backgroundImage === 'none') return;
        
        // Extract URL from background-image
        const imageUrl = extractBackgroundImageUrl(backgroundImage);
        if (!imageUrl) return;
        
        // Skip data URLs or blob URLs (already processed)
        if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) return;
        
        // Skip URLs from different domains (CORS issues)
        if (imageUrl.match(/^https?:\/\//) && !imageUrl.startsWith(window.location.origin)) return;
        
        // Mark as processed
        processedElements.add(element);
        
        log(`🖼️ Found CSS background image: ${imageUrl}`);
        
        try {
          // Check if image is already cached
          const options = getCompressionOptions(imageUrl, 1000000);
          const cacheKey = createCacheKey(imageUrl, options);
          
          if (imageCache.has(cacheKey)) {
            // Use cached version
            const cachedUrl = imageCache.get(cacheKey);
            element.style.backgroundImage = `url(${cachedUrl})`;
            log(`✅ Applied cached background image: ${imageUrl}`);
            stats.backgroundImagesOptimized++;
            return;
          }
          
          // Fetch and process the image
          const response = await fetch(imageUrl);
          if (!response.ok) return;
          
          const file = await response.blob();
          
          // Generate a blur placeholder
          let placeholderDataUrl = null;
          if (config.useProgressiveLoading) {
            placeholderDataUrl = await generateLQIP(file, imageUrl);
            
            if (placeholderDataUrl) {
              // Create unique class for this element
              const uniqueClass = `bg-loading-${Math.floor(Math.random() * 1000000)}`;
              element.classList.add(uniqueClass);
              
              // Create and append style
              const style = document.createElement('style');
              style.textContent = `
                .${uniqueClass} {
                  background-image: url("${placeholderDataUrl}") !important;
                  filter: blur(10px);
                  transition: filter 0.5s ease-out;
                }
                .${uniqueClass}.loaded {
                  filter: blur(0);
                }
              `;
              document.head.appendChild(style);
              
              // Apply the placeholder immediately
              element.style.backgroundImage = `url(${placeholderDataUrl})`;
            }
          }
          
          // Get final compression options
          const finalOptions = getCompressionOptions(imageUrl, file.size);
          
          // Compress the image
          const compressedFile = await imageCompression(file, finalOptions);
          
          // Create blob URL
          const blobUrl = URL.createObjectURL(compressedFile);
          
          // Cache the compressed image
          imageCache.set(cacheKey, blobUrl);
          
          // If we applied a placeholder, transition smoothly
          if (placeholderDataUrl) {
            // Create a temporary image to preload
            const tempImg = new Image();
            tempImg.onload = () => {
              // Apply final image and transition
              element.style.backgroundImage = `url(${blobUrl})`;
              element.classList.add('loaded');
              
              // Clean up after transition
              setTimeout(() => {
                const uniqueClass = Array.from(element.classList)
                  .find(c => c.startsWith('bg-loading-'));
                
                if (uniqueClass) {
                  element.classList.remove(uniqueClass, 'loaded');
                }
              }, 600);
            };
            tempImg.src = blobUrl;
          } else {
            // Apply directly without transition
            element.style.backgroundImage = `url(${blobUrl})`;
          }
          
          log(`✅ Optimized background image: ${imageUrl}`);
          stats.backgroundImagesOptimized++;
          
        } catch (error) {
          log(`Error processing background image: ${error.message}`, 'error');
        }
      } catch (err) {
        // Top-level error handling for any issues with getting computed style
        log(`Could not process background element: ${err.message}`, 'error');
      }
    };
    
    // Scan the DOM for elements with background images
    const scanForBackgroundImages = () => {
      // Find elements with explicit background-image style
      document.querySelectorAll('[style*="background-image"]').forEach(processBackgroundElement);
      
      // Find common elements that might have background images via CSS
      const commonBgElements = document.querySelectorAll('header, section, div.hero, div.banner, div.background, .bg-image');
      commonBgElements.forEach(processBackgroundElement);
    };
    
    // Create MutationObserver to watch for new elements
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          shouldCheck = true;
          break;
        }
        
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'style' && 
            mutation.target.style.backgroundImage) {
          processBackgroundElement(mutation.target);
        }
      }
      
      if (shouldCheck) {
        scanForBackgroundImages();
      }
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    // Initial scan
    scanForBackgroundImages();
    
    log(`🖼️ CSS background image optimization enabled`);
  };

  // Function to find and start observing new images with data-src attribute
  const findAndObserveNewImages = () => {
    // Find images with data-src that are not yet being observed
    const images = document.querySelectorAll('img[data-src]:not([data-observing])');
    log(`🔢 Found ${images.length} new images with data-src attribute`, 'info');
    
    // Start observing each image
    images.forEach(img => {
      // Mark as being observed to prevent observing the same image twice
      img.setAttribute('data-observing', 'true');
      
      // Add to the IntersectionObserver
      imageLoader.observe(img);
      
      // Log the observation
      const imgId = img.id || img.alt || img.dataset.src || 'unnamed';
      log(`👀 Started observing image ${imgId} with data-src: ${img.dataset.src}`, 'info');
    });
    
    return images.length;
  };
  
  try {
    // Create stats widget if enabled
    createStatsWidget();
    
    // Get all the images with data-src attribute
    const $lazyImages = document.querySelectorAll("img[data-src]");
    log(`🔢 Found ${$lazyImages.length} images with data-src attribute`);
    
    if ($lazyImages.length) {
      // For each image, check if it's already being processed
      $lazyImages.forEach((img, index) => {
        const imgURL = img.dataset.src;
        const imgId = img.id || img.alt || imgURL || `image-${index}`;
        
        // Skip if already being processed
        if (processingImages.has(imgURL)) {
          log(`⏭️ Skipping image ${imgId} as it's already being processed`);
          return;
        }
        
        // Mark as observed
        img.dataset.observed = "true";
        
        log(`👀 Started observing image ${imgId} with data-src: ${imgURL}`);
        imageLoader.observe(img);
      });
    } else {
      log(`⚠️ No images with data-src attribute found in the document`, 'warn');
    }
    
    // Look for priority images
    findPriorityImages();
    
    // Set up CSS background optimization
    setupCSSBackgroundOptimization();
    
    // Set up predictive prefetching
    setupPredictivePrefetching();
    
    // Set up a MutationObserver to detect dynamically added images with data-src
    const domObserver = new MutationObserver((mutations) => {
      let shouldCheckForImages = false;
      let shouldCheckForPriorityImages = false;
      
      // Check if any of the mutations might have added new images
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          shouldCheckForImages = true;
          shouldCheckForPriorityImages = true;
          break;
        }
      }
      
      // If we might have new images, check for them
      if (shouldCheckForImages) {
        findAndObserveNewImages();
      }
      
      // Check for priority images
      if (shouldCheckForPriorityImages) {
        findPriorityImages();
      }
    });
    
    // Start observing the document
    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Set up interval for viewport prefetching
    if (config.prefetchDistance > 0) {
      setInterval(checkViewportForPrefetching, 1000);
    }
    
    // Only set one timer for dynamically added images
    setTimeout(findAndObserveNewImages, 1500);
    
    // Set up window resize handler for responsive images
    if (config.responsive) {
      let resizeTimeout;
      window.addEventListener('resize', () => {
        // Debounce resize events
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          log('🔍 Window resize detected, updating responsive images');
          
          // Find all loaded data-src images
          const loadedImages = document.querySelectorAll('img[data-src][src]:not([src=""])');
          
          // Force reload of visible images with new dimensions
          loadedImages.forEach(img => {
            // Only reload visible images
            const rect = img.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0 &&
                rect.left < window.innerWidth && rect.right > 0) {
              
              // Get the original source
              const originalSrc = img.dataset.src;
              if (originalSrc && !processingImages.has(originalSrc)) {
                log(`🔄 Reloading image for new screen size: ${originalSrc}`);
                loadAndProcessImage(img);
              }
            }
          });
        }, 500);
      });
    }
    
    // Clean up image cache when navigating away
    window.addEventListener('beforeunload', () => {
      // Report final stats
      reportPerformanceStats();
      
      // Revoke all object URLs to prevent memory leaks
      imageCache.forEach(url => {
        URL.revokeObjectURL(url);
      });
    });
    
    // Listen for network changes to adapt compression settings for newly loaded images
    if ('connection' in navigator && 'addEventListener' in navigator.connection) {
      navigator.connection.addEventListener('change', () => {
        const network = getNetworkCondition();
        log(`🌐 Network conditions changed: ${network.type} (Save-Data: ${network.saveData ? 'On' : 'Off'})`);
      });
    }
    
  } catch (error) {
    log(`💥 Error setting up IntersectionObserver: ${error.message}`, 'error', error);
  }
  
  // Log browser support information
  log(`🔍 Browser support check:`, 'info', {
    'IntersectionObserver': 'IntersectionObserver' in window ? '✅ Supported' : '❌ Not supported',
    'Blob': 'Blob' in window ? '✅ Supported' : '❌ Not supported',
    'URL.createObjectURL': 'URL' in window && 'createObjectURL' in URL ? '✅ Supported' : '❌ Not supported',
    'fetch': 'fetch' in window ? '✅ Supported' : '❌ Not supported',
    'Network Information API': 'connection' in navigator ? '✅ Supported' : '❌ Not supported',
    'WebP': imageFormats.webp ? '✅ Supported' : '❌ Not supported',
    'AVIF': imageFormats.avif ? '✅ Supported' : '❌ Not supported',
    'LocalStorage': localStorageUtil.isAvailable ? '✅ Supported' : '❌ Not supported'
  });
  
  // Export controls for external use
  const controls = {
    // Force reload all images with current settings
    refreshAllImages: () => {
      document.querySelectorAll('img[data-src]').forEach(img => {
        const imgURL = img.dataset.src;
        if (imgURL && !processingImages.has(imgURL)) {
          loadAndProcessImage(img);
        }
      });
    },
    
    // Show/hide stats widget
    toggleStatsWidget: (show) => {
      const widget = document.getElementById('lazyload-stats-widget');
      if (widget) {
        widget.style.display = show ? 'block' : 'none';
      } else if (show) {
        config.showStats = true;
        createStatsWidget();
      }
    },
    
    // Get current stats
    getStats: () => ({ ...stats }),
    
    // Clear cache
    clearCache: () => {
      imageCache.clear();
      if (config.useLocalStorage && localStorageUtil.isAvailable) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key.startsWith(config.localStoragePrefix)) {
            localStorage.removeItem(key);
          }
        }
      }
      log('🧹 Cache cleared');
    },
    
    // Update configuration
    updateConfig: (newConfig) => {
      Object.assign(config, newConfig);
      log('⚙️ Configuration updated:', config);
    }
  };
  
  // Expose stats API for analytics
  window.getLazyLoadingStats = () => {
    reportPerformanceStats();
    return { ...stats };
  };
  
  // Expose controls globally
  window.lazyLoadingControls = controls;
  
  return controls;
}

/**
 * Safely creates a Web Worker with CSP compatibility
 * Uses a combination of blob and special URL handling to avoid CSP violations
 */
const createSafeWebWorker = (workerFunction) => {
  try {
    // Convert the function to a string
    const workerStr = workerFunction.toString();
    
    // Create a self-executing function string
    const blob = new Blob([
      `self.importScripts = function(url) {
        // Ensure URL is using HTTPS and absolute path
        if (url.startsWith('http://')) {
          url = url.replace('http://', 'https://');
        }
        if (!url.startsWith('http') && !url.startsWith('/')) {
          url = '/' + url;
        }
        return self.importScripts(url);
      };
      (${workerStr})();`
    ], { type: 'application/javascript' });
    
    // Instead of directly using the blob URL, we'll use a data URL
    // which is allowed by the CSP
    const blobUrl = URL.createObjectURL(blob);
    
    // Use a custom handler for Worker messages to avoid CSP issues
    // with direct worker creation
    const worker = new Worker(blobUrl);
    
    // Clean up the blob URL after creating the worker
    URL.revokeObjectURL(blobUrl);
    
    return worker;
  } catch (e) {
    log(`Failed to create Web Worker: ${e.message}`, 'error', e);
    return null;
  }
};